/** @jsx jsx */
import React from 'react'
import { Experiment, Fallback } from 'react-chansey'
import { RichPage } from './entry/rich-page'
import { Modular } from './entry/modular-page'
import sw from '../../utils/switch'
import { isObject, isEmpty } from '../../utils/obj'
import { useEntity, useExperiences, useMeta } from './selectors'
import { jsx, Themed } from 'theme-ui'
import { contentTypeId } from '../../utils/contentful'
import { Container, RichText, Link } from './react'
import { Type } from '@uswitch/spark'
import { getVariantPath } from '../../utils/get-variant-path'

const NA_FLAG_ID = 'not-applicable'

const stylePath = (field) =>
  getVariantPath({
    atomicType: 'compounds',
    componentId: 'author-snippet',
    field,
  })

const AuthorSnippet = ({ snippet, name: author, url }) => {
  const { mainPage, page, url: pressUrl } = useMeta(['authorSnippet', 'press'], {})

  return (
    <Container>
      <div sx={{ variant: stylePath('container') }}>
        <Type.H3 sx={{ variant: stylePath('title') }}>About {author}</Type.H3>

        <RichText content={snippet} />

        <Type.Body1>
          View <Link href={url}>{author}&apos;s full biography here</Link> or visit the {mainPage}{' '}
          <Link href={pressUrl}>{page}</Link> for our latest news.
        </Type.Body1>
      </div>
    </Container>
  )
}

const EVPage = (props) => {
  const entity = useEntity(props)

  if (entity.err) {
    // eslint-disable-next-line no-console
    console.log({ err: entity.err, props })
  }
  return sw({
    modularPage: Modular,
    richPage: RichPage,
  })(contentTypeId(entity), { ...props, ...entity })
}

const selectEntity = ({
  experienceFlag,
  experiences = {},
  entityMap = {},
  experienceList = [],
  entityList = [],
  fullPageTest = false,
} = {}) => {
  const experience = experiences[experienceFlag]

  // This condition is for the URL contentType with multiple variants
  // e.g. full pages tests
  if (fullPageTest) {
    return entityList[experience?.cohort || 0]
  }

  const experienceValue = experience?.value
  // Experience mapping based on mapping experienceFlag values to contentful
  // entity ids
  if (isObject(entityMap) && !isEmpty(entityMap)) {
    const entityId = entityMap[experienceValue] || entityMap[NA_FLAG_ID]
    return entityId ? { id: entityId } : null
  }

  // Legacy experience mapping based on indexes
  const entityIndex = experienceList.findIndex((experience) => experience === experienceValue)
  return entityList[entityIndex] || entityList[0] || null
}

const ExperiencePageSelector = (props) => {
  const experiences = useExperiences()
  const selectedEntity = selectEntity({ ...props, experiences })
  return (
    <Experiment id={props.experienceFlag}>
      <If condition={selectedEntity}>
        <Fallback>
          <EVPage {...selectedEntity} />
        </Fallback>
      </If>
    </Experiment>
  )
}

const Url = ({ link, variantFlag, variants = [], authors = [], editors = [] }) => {
  const entity = link || variants[0]
  const author = useEntity(authors[0] || editors[0])

  const { useSnippet } = useMeta(['authorSnippet'], { useSnippet: false })

  return (
    <>
      <Choose>
        <When condition={variants.length > 1}>
          <ExperiencePageSelector
            experienceFlag={variantFlag}
            entityList={variants}
            fullPageTest={true}
          />
        </When>

        <Otherwise>
          <EVPage {...entity} />
        </Otherwise>
      </Choose>

      <If condition={useSnippet && author?.sys?.id}>
        <AuthorSnippet {...author} />
      </If>
    </>
  )
}

export const RootEVEntry = (props) => {
  const entity = useEntity(props)
  const thing = { ...props, ...entity }
  return (
    <Themed.root>
      <Url {...thing} />
    </Themed.root>
  )
}
